import {
  type CategoryLeaf,
  type CategoryTreeNode,
  MAX_CATEGORY_NAME_LENGTH,
} from '@repo/api-contracts';
import { Fragment, type ReactElement, useState } from 'react';
import { useCategoriesQuery, useInvalidateCategories } from '@/entities/category';
import {
  CategoryChildList,
  CategoryRootRow,
  useCreateCategory,
  useDeleteCategory,
  useRenameCategory,
  useReorderCategories,
} from '@/features/categories';
import { movedOrder, type RenameTarget, resetSettledRequests } from '@/shared/lib';
import { Button, ContentCard, cn, EmptyState, NameFormModal, tableStyles } from '@/shared/ui';

/**
 * 카테고리 마스터 — 와이어프레임의 표(노출 순서·카테고리명·하위 품목·관리) 형태.
 * 대분류 행·소분류 칩 모두 드래그로 순서를 바꾼다. 소분류는 대분류 행을 펼쳐
 * 그 컨텍스트 안에서 추가·수정·삭제한다 (전역 추가 버튼과의 혼동 방지).
 */
export const CategoryMasterSection = (): ReactElement => {
  const invalidate = useInvalidateCategories();
  const categoriesQuery = useCategoriesQuery();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [expandedRootId, setExpandedRootId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [newChildName, setNewChildName] = useState('');
  const [dragRootIndex, setDragRootIndex] = useState<number | null>(null);

  const createAction = useCreateCategory({
    onSuccess: () => {
      invalidate();
      setIsAddModalOpen(false);
      setNewChildName('');
    },
  });
  const renameAction = useRenameCategory({
    onSuccess: () => {
      invalidate();
      setRenameTarget(null);
    },
  });
  const reorderAction = useReorderCategories({ onSuccess: invalidate });
  const deleteAction = useDeleteCategory({ onSuccess: invalidate });

  const mutationError =
    (isAddModalOpen ? null : createAction.error) ??
    renameAction.error ??
    reorderAction.error ??
    deleteAction.error;

  // 추가·이름 수정·순서 변경·삭제는 오류 한 줄(mutationError)을 함께 쓴다(추가 모달이 열려 있는 동안의
  // 추가 오류는 모달이 보여 준다). 새 요청을 보내기 전에 네 요청의 지난 오류를 모두 지운다 —
  // 아니면 다른 요청을 해도 옛 오류가 그 요청이 다시 돌 때까지 남는다. 행에는 요청 사이의 잠금이 없어
  // 다른 요청이 진행 중일 수 있다 — 진행 중인 요청은 지우지 않는다.
  const clearMutationErrors = (): void => {
    resetSettledRequests([createAction, renameAction, reorderAction, deleteAction]);
  };

  // 드래그 드랍 시 형제 전체 순서 제출용 — 렌더당 1회만 계산한다.
  const rootIds = (categoriesQuery.data ?? []).map((node) => node.id);

  const rename = {
    target: renameTarget
      ? { ...renameTarget, isPending: renameAction.pendingId === renameTarget.id }
      : null,
    onChange: (name: string) =>
      setRenameTarget((current) => (current ? { ...current, name } : current)),
    onSubmit: () => {
      if (renameTarget?.name.trim()) {
        clearMutationErrors();
        renameAction.renameCategory({ id: renameTarget.id, name: renameTarget.name.trim() });
      }
    },
    onCancel: () => setRenameTarget(null),
  };

  const handleDelete = (category: CategoryLeaf, isRoot: boolean): void => {
    const scope = isRoot ? '대분류(하위 소분류 포함)' : '소분류';

    if (window.confirm(`${scope} "${category.name}" 을(를) 삭제하시겠습니까?`)) {
      clearMutationErrors();
      deleteAction.deleteCategory({ id: category.id });
    }
  };

  const handleToggleExpand = (root: CategoryTreeNode): void => {
    setExpandedRootId((current) => (current === root.id ? null : root.id));
    setNewChildName('');
  };

  const handleChildAdd = (parentId: string): void => {
    if (newChildName.trim()) {
      clearMutationErrors();
      createAction.createCategory({ name: newChildName.trim(), parentId });
    }
  };

  const handleRootAdd = (name: string): void => {
    clearMutationErrors();
    createAction.createCategory({ name, parentId: null });
  };

  const handleChildReorder = (parentId: string, orderedIds: readonly string[]): void => {
    clearMutationErrors();
    reorderAction.reorderCategories({ parentId, orderedIds: [...orderedIds] });
  };

  const rootDrag = {
    draggingIndex: dragRootIndex,
    onDragStart: setDragRootIndex,
    onDrop: (index: number): void => {
      if (dragRootIndex !== null && dragRootIndex !== index) {
        clearMutationErrors();
        reorderAction.reorderCategories({
          parentId: null,
          orderedIds: movedOrder(rootIds, dragRootIndex, index),
        });
      }
      setDragRootIndex(null);
    },
    onDragEnd: () => setDragRootIndex(null),
  };

  return (
    <ContentCard
      title="카테고리"
      actions={
        <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(true)}>
          + 카테고리 추가
        </Button>
      }
    >
      {mutationError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {categoriesQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : categoriesQuery.isError ? (
        <EmptyState isError>
          카테고리를 불러오지 못했습니다: {categoriesQuery.error.message}
        </EmptyState>
      ) : categoriesQuery.data.length === 0 ? (
        <EmptyState>등록된 카테고리가 없습니다.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={cn(tableStyles.th, 'w-28')}>노출 순서</th>
                <th className={tableStyles.th}>카테고리명</th>
                <th className={tableStyles.th}>하위 품목</th>
                <th className={cn(tableStyles.th, 'w-56')}>관리</th>
              </tr>
            </thead>
            <tbody>
              {categoriesQuery.data.map((root, index) => (
                <Fragment key={root.id}>
                  <CategoryRootRow
                    root={root}
                    index={index}
                    isExpanded={expandedRootId === root.id}
                    drag={rootDrag}
                    rename={rename}
                    onRenameStart={(category) =>
                      setRenameTarget({ id: category.id, name: category.name })
                    }
                    onToggleExpand={handleToggleExpand}
                    onDelete={(target) => handleDelete(target, true)}
                  />
                  {expandedRootId === root.id ? (
                    <CategoryChildList
                      root={root}
                      rename={rename}
                      onRenameStart={(category) =>
                        setRenameTarget({ id: category.id, name: category.name })
                      }
                      onDelete={(category) => handleDelete(category, false)}
                      onReorder={handleChildReorder}
                      addForm={{
                        value: newChildName,
                        isPending: createAction.isPending,
                        onChange: setNewChildName,
                        onSubmit: () => handleChildAdd(root.id),
                      }}
                    />
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        행을 끌어서 노출 순서를 바꾸면 고객이 보는 상품 목록·홈 화면의 카테고리 순서도 함께
        바뀝니다. 공개 상품이 유일하게 속한 카테고리는 삭제할 수 없습니다.
      </p>

      {isAddModalOpen ? (
        <NameFormModal
          title="카테고리 추가"
          label="카테고리명"
          placeholder="예: Stationery"
          maxLength={MAX_CATEGORY_NAME_LENGTH}
          isPending={createAction.isPending}
          errorMessage={createAction.error?.message ?? null}
          onSubmit={handleRootAdd}
          onClose={() => setIsAddModalOpen(false)}
        />
      ) : null}
    </ContentCard>
  );
};
